import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AuthSevices,
  StaffNotificationService,
  ToastMessageService,
} from '@fitness-force/api';
import { NotificationLevel, NotificationPreference } from '@fitness-force/enum';
import type {
  PatchStaffNotificationTopics,
  StaffNotification,
  StaffNotificationTopics,
  TENANTSINTERFACE,
} from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import { environment } from '@fitness-force/environments';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DrawerModule } from 'primeng/drawer';
import * as PusherPushNotifications from '@pusher/push-notifications-web';
import { SkeletonModule } from 'primeng/skeleton';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { FeatureFlagService } from '@fitness-force/shared-utils';
// import Statsig from 'statsig-js';

@Component({
  selector: 'staff-notification',
  standalone: true,
  imports: [
    DrawerModule,
    ToggleSwitchModule,
    FormsModule,
    ReactiveFormsModule,
    SkeletonModule,
    TranslateModule,
    ButtonModule,
    SelectModule,
    MessageModule,
  ],
  templateUrl: './staff-notification.component.html',
})
export class StaffNotificationComponent implements OnInit {
  private readonly staffNotificationService = inject(StaffNotificationService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly toastMessageService = inject(ToastMessageService);
  translate = inject(TranslateService);
  private readonly authService = inject(AuthSevices);
  private readonly featureFlagService = inject(FeatureFlagService);

  notificationsForm: FormGroup = {} as FormGroup;
  staffSubscribedNotification: StaffNotification = {} as StaffNotification;
  allNotifications: { key: string; label: string }[] = [];
  tenantAccess: any[] = [];
  tenantId: number = 0;
  companyId: number = 0;
  initializeNotificationSdk: boolean = false;
  showSkeleton: boolean = true;
  NotificationLevelEnum = Object.entries(NotificationLevel)
    .filter((key) => typeof key[1] != 'number')
    .map(([key, value]) => ({ key, value }));
  currentUserDetails: any;
  notificationsBlocked: boolean = false;
  hasSubscribedTopics: boolean = false;

  ngOnInit() {
    this.currentUserDetails = this.authService.currentUserDetails();
    this.showSkeleton = true;
    this.notificationsForm = this.fb.group({});
    this.NotificationLevelEnum = this.NotificationLevelEnum.map((o) => {
      return { key: o.key, value: o.value, disabled: false };
    });
    //this.validateClaims();
    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantAccess = tenant.accessed_tenant;
      this.tenantId = tenant.selected_tenant.id;
      this.companyId = tenant.selected_tenant.company_id;
    });
    this.getNotifications();
    this.checkNotificationPermission();
    this.cdr.detectChanges();
  }

  async checkNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.permission;
      this.notificationsBlocked = permission === 'denied';
      this.cdr.detectChanges();
    }
  }

  getNotifications() {
    this.staffNotificationService.getNotifications().subscribe({
      next: async (res) => {
        this.staffSubscribedNotification = res;

        this.staffSubscribedNotification.subscribed_topics =
          this.staffSubscribedNotification.subscribed_topics.filter(
            (topic) =>
              topic.notificationSubscriptionTopic !==
              'SignupCompletionTargetDevice',
          );
        if (this.staffSubscribedNotification.subscribed_topics.length === 0) {
          this.initializeNotificationSdk = true;
        }
        this.hasSubscribedTopics =
          this.staffSubscribedNotification.subscribed_topics.some(
            (topic) => topic.isSubscribed,
          );
        this.allNotifications = this.getEnumValues(NotificationPreference);
        await this.createFormControls();
        this.showSkeleton = false;
        this.cdr.detectChanges();
      },
      error: async (err) => {
        await this.createFormControls();
        this.showSkeleton = false;
        this.toastMessageService.showError(err);
        this.cdr.detectChanges();
      },
    });
  }

  getNotificationLevelOptions(topic: string) {
    const topics: any = ['IncomingSMS', 'IncomingEmail', 'IncomingWhatsApp'];
    if (
      this.currentUserDetails?.Id &&
      topics.includes(topic)
    ) {
      if (this.currentUserDetails.role === 'Admin') {
        return this.NotificationLevelEnum.map((o) => {
          return { key: o.key, value: o.value, disabled: false };
        });
      } else if (this.currentUserDetails.role === 'Staff') {
        const canAdminMemberCommunication =
          this.currentUserDetails['can_admin_membercommunication'] == 'true';
        if (canAdminMemberCommunication) {
          return this.NotificationLevelEnum.map((o) => {
            return { key: o.key, value: o.value, disabled: false };
          });
        } else {
          return this.NotificationLevelEnum.map((o) => {
            if (['All', 'Department'].includes(o.value.toString())) {
              return { key: o.key, value: o.value, disabled: true };
            }
            return { key: o.key, value: o.value, disabled: false };
          });
        }
      } else
        return this.NotificationLevelEnum.map((o) => {
          return { key: o.key, value: o.value, disabled: false };
        });
    } else {
      return this.NotificationLevelEnum.map((o) => {
        return { key: o.key, value: o.value, disabled: false };
      });
    }
  }

  async createFormControls(): Promise<void> {
    const excludeTopics: any[] = [
      'IncomingSMS',
      'IncomingEmail',
      'IncomingWhatsApp',
    ];
    // Wait for Statsig initialization before checking flags
    const isTwoWayNotificationEnabled =
      await this.featureFlagService.isEnabledAsync(
        'show_staff_2way_message_notifciation',
      );
    const isDesktopNotificatuonEnabled =
      await this.featureFlagService.isEnabledAsync(
        'enable_staff_desktop_notification',
      );
    const subscribedTopics =
      this.staffSubscribedNotification?.subscribed_topics || [];
    const formControls = subscribedTopics.reduce(
      (controls: any, notification) => {
        const level = notification.notificationSubscriptionTopic + '_level';
        const isSubscribed = this.staffSubscribedNotification.subscribed_topics
          ? this.staffSubscribedNotification.subscribed_topics.find((o) => {
              return (
                o.notificationSubscriptionTopic ===
                notification.notificationSubscriptionTopic
              );
            })?.isSubscribed
          : false;
        const control = new FormControl(isSubscribed, []);
        let notificationLevel = this.NotificationLevelEnum.find((o) => {
          return Number(o.key) === Number(notification.notificationLevel);
        });
        if (!notificationLevel) {
          notificationLevel = this.NotificationLevelEnum.find(
            (o) => o.value === 'Self',
          );
        }
        const notificationLevelControl = new FormControl(notificationLevel, [
          Validators.required,
        ]);
        controls[notification.notificationSubscriptionTopic] = control;
        controls[level] = notificationLevelControl;

        if (
          excludeTopics.includes(notification.notificationSubscriptionTopic)
        ) {
          controls[level].enable();
        } else {
          controls[level].setValue(
            this.NotificationLevelEnum.find((o) => {
              return o.value == 'All';
            }),
          );
          controls[level].disable();
          controls[level].updateValueAndValidity();
        }

        if (
          !isTwoWayNotificationEnabled &&
          excludeTopics.includes(notification.notificationSubscriptionTopic)
        ) {
          controls[notification.notificationSubscriptionTopic].disable();
          controls[
            notification.notificationSubscriptionTopic
          ].updateValueAndValidity();
        } else if (
          !isDesktopNotificatuonEnabled &&
          !isTwoWayNotificationEnabled
        ) {
          controls[level].disable();
          controls[level].updateValueAndValidity();
          controls[notification.notificationSubscriptionTopic].disable();
          controls[
            notification.notificationSubscriptionTopic
          ].updateValueAndValidity();
        } else if (
          !isDesktopNotificatuonEnabled &&
          isTwoWayNotificationEnabled
        ) {
          if (
            excludeTopics.includes(notification.notificationSubscriptionTopic)
          ) {
            controls[level].enable();
            controls[notification.notificationSubscriptionTopic].enable();
            controls[
              notification.notificationSubscriptionTopic
            ].updateValueAndValidity();
          } else {
            controls[level].disable();
            controls[notification.notificationSubscriptionTopic].disable();
            controls[level].updateValueAndValidity();
            controls[
              notification.notificationSubscriptionTopic
            ].updateValueAndValidity();
          }
        }
        return controls;
      },
      {},
    );
    this.notificationsForm = this.fb.group(formControls);
  }

  getEnumValues(enumObj: any): { key: string; label: string }[] {
    return Object.keys(enumObj)
      .filter((key) => isNaN(Number(key))) // Filter out numeric keys
      .map((key) => ({ key, label: this.formatLabel(key) }));
  }

  formatLabel(key: string): string {
    return key.replace(/([A-Z])/g, ' $1').trim();
  }

  UpdateValue(key: string, event: any) {
    this.onNotificationChange(key, event.checked);
  }

  UpdateLevel(key: string, event: any) {
    if (this.notificationsForm.get(key)?.value)
      this.onNotificationChange(key, this.notificationsForm.get(key)?.value);
  }

  onNotificationChange(key: string, value: boolean): void {
    if (
      this.notificationsForm.get(key + '_level')?.value?.key
    ) {
      const request: Array<{ op: string; path: string; value: any }> = [];
      const payload: PatchStaffNotificationTopics[] =
        [] as PatchStaffNotificationTopics[];
      payload.push({
        NotificationSubscriptionTopic: key,
        IsSubscribed: value,
        NotificationLevel: Number(
          this.notificationsForm.get(key + '_level')?.value.key,
        ),
      });
      request.push({
        op: 'replace',
        path: '/CreateUpdateStaffSubscribedNotificationRequest',
        value: payload,
      });
      this.staffNotificationService.patchNotifications(request).subscribe({
        next: () => {
          if (value) {
            this.translate
              .get('STAFF.STAFF_PREFERENCE.TOPIC_SUBSCRIBED')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
          } else {
            this.translate
              .get('STAFF.STAFF_PREFERENCE.TOPIC_UNSUBSCRIBED')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
          }
          this.staffSubscribedNotification.subscribed_topics.forEach(
            (notification) => {
              if (notification.notificationSubscriptionTopic === key) {
                notification.isSubscribed = value;
              }
            },
          );
          this.store.dispatch({
            type: 'FETCH_STAFF_NOTIFICATIONS',
            staffNotifications: this.staffSubscribedNotification,
            companyId: this.companyId,
          });
          if (this.initializeNotificationSdk) {
            this.ConfigurePusher();
          }
        },
        error: (error) => {
          this.toastMessageService.showError(error);
        },
      });
    } else {
      this.notificationsForm.get(key + '_level')?.setErrors({ required: true });
      this.notificationsForm.get(key + '_level')?.markAsTouched();
      this.notificationsForm.get(key)?.setValue(false);
    }
  }

  ConfigurePusher() {
    const beamsClient = new PusherPushNotifications.Client({
      instanceId: environment.PUSHER_INSTANCE_ID,
    });

    beamsClient
      .start()
      .then((beamsClient: any) => beamsClient.getDeviceId())
      .then((deviceId) => {
        // Successfully registered with Beams
        if (typeof window !== 'undefined' && (window as any).__DEBUG__) {
          // console.log(
          //   'Successfully registered with Beams. Device ID:',
          //   deviceId,
          // );
        }
      })
      .then(() => {
        this.staffSubscribedNotification.tenant_ids.forEach((t) => {
          this.staffSubscribedNotification.subscribed_topics.forEach(
            (topic) => {
              if (topic.isSubscribed)
                beamsClient.addDeviceInterest(
                  `${this.companyId}_${t}_${topic.notificationSubscriptionTopic}`,
                );
            },
          );
        });
      })
      .then(() => beamsClient.getDeviceInterests())
      .catch((error: any) => {
        // Handle permission denied errors gracefully - this is expected when notifications are disabled
        const errorName = error?.name?.toLowerCase() || '';
        const errorMessage = error?.message?.toLowerCase() || '';

        if (
          errorName === 'notallowederror' &&
          (errorMessage.includes('registration failed') ||
            errorMessage.includes('permission denied') ||
            errorMessage.includes('permission_denied'))
        ) {
          // Silently ignore - expected behavior when notifications are denied
          return;
        }

        // Log other unexpected errors
        console.error('Failed to configure Pusher Beams:', error);
      });
  }

  disableAll() {
    if (
      this.staffSubscribedNotification.subscribed_topics.filter(
        (o) => o.isSubscribed,
      ).length > 0
    ) {
      const request: Array<{ op: string; path: string; value: any }> = [];
      const payload: StaffNotificationTopics[] = [] as StaffNotificationTopics[];
      this.staffSubscribedNotification.subscribed_topics.forEach(
        (notification) => {
          this.notificationsForm
            .get(notification.notificationSubscriptionTopic)
            ?.setValue(false);
          payload.push({
            notificationSubscriptionTopic:
              notification.notificationSubscriptionTopic,
            isSubscribed: false,
            notificationLevel: notification.notificationLevel,
          });
        },
      );
      request.push({
        op: 'replace',
        path: '/PushNotificationInterests',
        value: payload,
      });
      this.staffNotificationService.patchNotifications(request).subscribe({
        next: () => {
          this.translate
            .get('STAFF.STAFF_PREFERENCE.DISABLE_ALL_SUCCESS')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
        },
        error: (error) => {
          this.toastMessageService.showError(error);
        },
      });
    }
  }

  public get showDisableAll() {
    return (
      this.staffSubscribedNotification.subscribed_topics &&
      this.staffSubscribedNotification.subscribed_topics.filter(
        (o) => o.isSubscribed,
      ).length > 0
    );
  }

  getFormControl(key: string): FormControl {
    return this.notificationsForm.get(key) as FormControl;
  }
}
